import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { BrandedLoader } from '@/components/Loader';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Repeat, Trash2, ChevronUp, ChevronDown, GripVertical, Clock } from 'lucide-react';
import { toast } from 'sonner';

const CADENCE = { daily: 'Diaria', weekly: 'Semanal', monthly: 'Mensual', custom: 'Personalizada' };

export default function RoutinesPage() {
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { name: '', color: '#3EB489', cadence: 'weekly', description: '', steps: [] };
  const [form, setForm] = useState(blank);

  const load = async () => { setLoading(true); const { data } = await api.get('/routines'); setRoutines(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const addStep = () => setForm({ ...form, steps: [...form.steps, { title: '', duration: '' }] });
  const setStep = (i, k, v) => setForm({ ...form, steps: form.steps.map((s, j) => j === i ? { ...s, [k]: v } : s) });
  const rmStep = (i) => setForm({ ...form, steps: form.steps.filter((_, j) => j !== i) });
  const move = (i, dir) => { const arr = [...form.steps]; const j = i + dir; if (j < 0 || j >= arr.length) return; [arr[i], arr[j]] = [arr[j], arr[i]]; setForm({ ...form, steps: arr }); };

  const save = async () => {
    if (!form.name) return toast.error('Nombre requerido');
    const payload = { ...form, steps: form.steps.filter((s) => s.title) };
    if (editing) await api.patch(`/routines/${editing.id}`, payload);
    else await api.post('/routines', payload);
    toast.success(editing ? 'Rutina actualizada' : 'Rutina creada'); setOpen(false); load();
  };
  const del = async (id) => { await api.delete(`/routines/${id}`); load(); };

  if (loading) return <BrandedLoader label="Cargando rutinas..." />;

  return (
    <div data-testid="routines-module">
      <PageHeader kicker="Estructura" title="Rutinas" subtitle="Crea estructuras para cursos y reuniones frecuentes. Reordena los pasos cuando quieras."
        actions={<button data-testid="new-routine-btn" onClick={openNew} className="h-10 px-4 rounded-xl text-sm font-medium text-[#07090A] flex items-center gap-2" style={{ background: '#3EB489' }}><Plus size={16} /> Rutina</button>} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {routines.map((r) => (
          <motion.div key={r.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="pp-glass p-5" data-testid={`routine-${r.id}`} style={{ borderLeft: `4px solid ${r.color}` }}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2"><Repeat size={16} style={{ color: r.color }} /><span className="font-semibold">{r.name}</span></div>
              <div className="flex gap-1"><button onClick={() => openEdit(r)} className="text-xs pp-mint">Editar</button><button onClick={() => del(r.id)} className="text-red-400"><Trash2 size={14} /></button></div>
            </div>
            <div className="text-[11px] text-muted-foreground mb-3">{CADENCE[r.cadence]} · {r.steps.length} pasos</div>
            <ol className="space-y-1.5">
              {r.steps.map((s, i) => (
                <li key={s.id || i} className="flex items-center gap-2 text-sm">
                  <span className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold" style={{ background: r.color + '33', color: r.color }}>{i + 1}</span>
                  <span className="flex-1">{s.title}</span>
                  {s.duration && <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock size={10} />{s.duration}</span>}
                </li>
              ))}
            </ol>
          </motion.div>
        ))}
        {routines.length === 0 && <div className="pp-glass p-8 text-center text-muted-foreground col-span-full">Sin rutinas. Crea la primera (ej. "Preparaci\u00f3n de clase").</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="pp-glass-strong max-w-lg"><DialogHeader><DialogTitle>{editing ? 'Editar rutina' : 'Nueva rutina'}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-auto pp-scroll">
            <input data-testid="routine-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre de la rutina" className="w-full h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none focus:border-[#3EB489]" />
            <div className="flex gap-2 items-center">
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-11 w-16 rounded-xl bg-transparent border" />
              <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })} className="flex-1 h-11 px-3 rounded-xl bg-[rgba(255,255,255,0.05)] border outline-none">{Object.entries(CADENCE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium">Pasos</span><button data-testid="add-step" onClick={addStep} className="text-xs pp-mint flex items-center gap-1"><Plus size={13} /> Paso</button></div>
              <div className="space-y-2">
                {form.steps.map((s, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <div className="flex flex-col"><button onClick={() => move(i, -1)} className="text-muted-foreground hover:pp-mint"><ChevronUp size={14} /></button><button onClick={() => move(i, 1)} className="text-muted-foreground hover:pp-mint"><ChevronDown size={14} /></button></div>
                    <input data-testid={`step-title-${i}`} value={s.title} onChange={(e) => setStep(i, 'title', e.target.value)} placeholder={`Paso ${i + 1}`} className="flex-1 h-10 px-3 rounded-lg bg-[rgba(255,255,255,0.05)] border outline-none" />
                    <input value={s.duration} onChange={(e) => setStep(i, 'duration', e.target.value)} placeholder="15m" className="w-16 h-10 px-2 rounded-lg bg-[rgba(255,255,255,0.05)] border outline-none text-xs" />
                    <button onClick={() => rmStep(i)} className="text-red-400"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
            <button data-testid="routine-save" onClick={save} className="w-full h-11 rounded-xl font-semibold text-[#07090A]" style={{ background: '#3EB489' }}>Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
